import React, { createContext, useContext, useMemo } from 'react';
import { loader } from 'graphql.macro';
import { NetworkStatus, useQuery } from '@apollo/client';
import { useWorkspace } from './WorkspaceProvider';
import { useCurrentUser } from './UserProvider';
import { useState } from 'react';
import CustomSpinner from '../components/CustomSpinner';
import { useOnlineStatus } from '../hooks/offline-hooks/offline-misc';

const workspacePermissionsQuery = loader(
  './WorkspacePermissionsProvider.graphql',
);

const WorkspacePermissionsContext = createContext();

export const WorkspacePermissionsProvider = ({ children }) => {
  const { workspaceId, refetchWorkspacePermission, workspacePermissions } =
    useWorkspace();
  const { user, syncing } = useCurrentUser();
  const [changingWorkspace, setChangingWorkspace] = useState(false);
  const online = useOnlineStatus();

  const {
    refetch,
    networkStatus,
    data: { users = [] } = {},
  } = useQuery(workspacePermissionsQuery, {
    skip: !workspaceId || !online || syncing,
    notifyOnNetworkStatusChange: true,
  });

  const workspaceAdmins = useMemo(() => {
    return (
      workspacePermissions
        ?.filter((p) => p.accessLevel === 'ADMIN')
        .map((u) => u.user) || []
    );
  }, [workspacePermissions]);

  const workspaceUsers = useMemo(() => {
    return (
      workspacePermissions
        ?.filter((p) => p.accessLevel === 'USER')
        .map((u) => u.user) || []
    );
  }, [workspacePermissions]);

  const loading = networkStatus === NetworkStatus.loading && users?.length < 0;

  const isWorkspaceAdmin = workspaceAdmins
    .map(({ id }) => id)
    .includes(user.id);

  const companyAdmins = users?.filter((u) => u.role === 'ADMIN') || [];

  return (
    <WorkspacePermissionsContext.Provider
      value={{
        workspaceAdmins,
        workspaceUsers,
        allWorkspaceUsers: workspacePermissions?.map((u) => u.user) || [],
        refetchPermissions: refetchWorkspacePermission,
        setChangingWorkspace,
        changingWorkspace,
        allUsers: users || [],
        isWorkspaceAdmin,
        companyAdmins,
        refetchAllUsers: refetch,
      }}
    >
      {(loading || changingWorkspace) && (
        <CustomSpinner text={'Loading Permissions...'} />
      )}
      {children}
    </WorkspacePermissionsContext.Provider>
  );
};

export const useWorkspacePermissions = () =>
  useContext(WorkspacePermissionsContext);
